
import React, { useState, useMemo, FC } from 'react';
import type { Shift, Schedule, StaffMember, AdminUser } from '../../types';
import { formatDateYYYYMMDD } from '../../utils/dateUtils';
import { Icons } from '../ui/Icons';

type SchedulablePerson = (StaffMember & { uid?: never }) | (Omit<AdminUser, 'uid'> & { id: string });

interface WorkforceTrackerProps {
    people: SchedulablePerson[];
    shifts: Shift[];
    schedule: Schedule;
    onBack?: () => void;
}

type SearchMode = 'now' | 'specific';

// Converts "HH:mm" time string to minutes since midnight.
const toMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
};

// Represents a staff member who is currently working, along with their shift info.
type WorkingStaffInfo = {
    person: SchedulablePerson;
    shift: Shift;
};

const WorkforceTracker: FC<WorkforceTrackerProps> = ({ people, shifts, schedule, onBack }) => {
    const [searchMode, setSearchMode] = useState<SearchMode>('now');
    
    // State for specific date/time search. Defaults to current time.
    const [selectedDate, setSelectedDate] = useState<string>(() => formatDateYYYYMMDD(new Date()));
    const [selectedTime, setSelectedTime] = useState<string>(() => new Date().toTimeString().substring(0, 5));

    const workingStaff = useMemo<WorkingStaffInfo[]>(() => {
        let searchDate: string;
        let searchTime: string;

        if (searchMode === 'now') {
            const now = new Date();
            searchDate = formatDateYYYYMMDD(now);
            searchTime = now.toTimeString().substring(0, 5);
        } else {
            searchDate = selectedDate;
            searchTime = selectedTime;
        }

        if (!searchDate || !searchTime) {
            return [];
        }

        const searchTimeInMinutes = toMinutes(searchTime);
        const shiftsMap = new Map<string, Shift>(shifts.map(s => [s.id, s]));
        const results: WorkingStaffInfo[] = [];

        // Calculate previous date to check for overnight shifts starting yesterday
        const searchDateObj = new Date(searchDate + 'T00:00:00');
        const prevDateObj = new Date(searchDateObj);
        prevDateObj.setDate(prevDateObj.getDate() - 1);
        const prevDate = formatDateYYYYMMDD(prevDateObj);

        for (const person of people) {
            // 1. Check today's schedule
            const todayEntry = schedule[person.id]?.[searchDate];
            if (todayEntry && todayEntry.status === 'ASSIGNED' && todayEntry.shiftId) {
                const shift = shiftsMap.get(todayEntry.shiftId);
                if (shift) {
                    const start = toMinutes(shift.startTime);
                    const end = toMinutes(shift.endTime);
                    
                    if (end > start) {
                        // Normal shift: searchTime must be between start and end
                        if (searchTimeInMinutes >= start && searchTimeInMinutes < end) {
                            results.push({ person, shift });
                            continue; // Found them working today's shift
                        }
                    } else {
                        // Overnight shift starting today: searchTime must be >= start
                        if (searchTimeInMinutes >= start) {
                            results.push({ person, shift });
                            continue;
                        }
                    }
                }
            }

            // 2. Check yesterday's schedule for overnight shifts ending today
            const yesterdayEntry = schedule[person.id]?.[prevDate];
            if (yesterdayEntry && yesterdayEntry.status === 'ASSIGNED' && yesterdayEntry.shiftId) {
                const shift = shiftsMap.get(yesterdayEntry.shiftId);
                if (shift) {
                    const start = toMinutes(shift.startTime);
                    const end = toMinutes(shift.endTime);
                    
                    if (end < start) {
                        // Overnight shift starting yesterday: searchTime must be < end
                        if (searchTimeInMinutes < end) {
                            results.push({ person, shift });
                        }
                    }
                }
            }
        }

        return results;

    }, [searchMode, selectedDate, selectedTime, people, shifts, schedule]);

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedDate(e.target.value);
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedTime(e.target.value);
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-4">
                {onBack && (
                    <button
                        onClick={onBack}
                        className="p-2 rounded-lg bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shadow-sm"
                        title="Back to Scheduler"
                    >
                        <Icons.ArrowLeft className="w-5 h-5" />
                    </button>
                )}
                <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Workforce Tracker</h2>
            </div>
            <div className="bg-white dark:bg-slate-800 p-6 rounded-lg shadow-md">
                <div className="flex flex-col sm:flex-row gap-4">
                    {/* Search Controls */}
                    <div className="flex-1 space-y-4">
                        <div className="flex items-center gap-2 bg-gray-100 dark:bg-slate-900 p-1 rounded-lg">
                            <button
                                onClick={() => setSearchMode('now')}
                                className={`flex-1 text-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${searchMode === 'now' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700/50'}`}
                            >
                                Who is working now?
                            </button>
                            <button
                                onClick={() => setSearchMode('specific')}
                                className={`flex-1 text-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors duration-150 ${searchMode === 'specific' ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-slate-700/50'}`}
                            >
                                Check specific time
                            </button>
                        </div>

                        {searchMode === 'specific' && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label htmlFor="search-date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                                    <input
                                        type="date"
                                        id="search-date"
                                        value={selectedDate}
                                        onChange={handleDateChange}
                                        className="w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="search-time" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Time</label>
                                    <input
                                        type="time"
                                        id="search-time"
                                        value={selectedTime}
                                        onChange={handleTimeChange}
                                        className="w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-200"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="w-full sm:w-px bg-gray-200 dark:bg-slate-700 mx-0 sm:mx-6 my-4 sm:my-0"></div>

                    {/* Results */}
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Results ({workingStaff.length})</h3>
                        <div className="bg-gray-50 dark:bg-slate-900/50 p-3 rounded-md min-h-[120px]">
                            {workingStaff.length > 0 ? (
                                <ul className="space-y-2">
                                    {workingStaff.map(({ person, shift }) => (
                                        <li key={person.id} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded shadow-sm">
                                            <div>
                                                <p className="font-medium text-gray-900 dark:text-gray-100">{person.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{person.role}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-semibold text-white px-2 py-0.5 rounded" style={{backgroundColor: shift.color}}>{shift.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{shift.startTime} - {shift.endTime}</p>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-gray-400">
                                    <Icons.Users className="w-8 h-8 mb-2" />
                                    <p className="font-medium">No staff working at this time.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkforceTracker;